"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.LocalWorkflowExecutionManager = void 0;
var types_core_1 = require("@augloop/types-core");
var interfaces_1 = require("@augloop/interfaces");
var logging_1 = require("@augloop/logging/dist/src/client/logging");
var session_protocol_1 = require("@augloop/session-protocol");
var LocalWorkflowExecutionManager = /** @class */ (function () {
    function LocalWorkflowExecutionManager(session, modelDownloader) {
        this.localWorkflows = [];
        this.session = session;
        this.nextAnnotationId = 1;
        this.nextSignalId = 1;
        this.modelDownloader = modelDownloader;
    }
    LocalWorkflowExecutionManager.prototype.getNextClientAnnotationId = function () {
        return '#AC' + this.nextAnnotationId++;
    };
    LocalWorkflowExecutionManager.prototype.getNextClientSignalId = function () {
        return '#SC' + this.nextSignalId++;
    };
    LocalWorkflowExecutionManager.prototype.registerLocalWorkflow = function (workflow) {
        var op = new logging_1.Operation({
            operationName: 'WorkflowRegistration',
            resourceId: workflow.id
        }).start();
        if (workflow.inputTypes.length === 0) {
            throw new Error("Invalid workflow params");
        }
        else {
            var workflowLambda = workflow.factory();
            var wfImplementation = {
                workflow: workflow,
                workflowLambda: workflowLambda,
                initPromise: this.initWorkflow(workflow.kind, workflowLambda)
            };
            this.localWorkflows.push(wfImplementation);
            op.success = true;
            logging_1.Log.info(0x2224d0de /* tag_8jnd4 */, logging_1.LogCategory.CoreDefault, op.stop());
        }
    };
    LocalWorkflowExecutionManager.prototype.runLocalWorkflows = function (ops) {
        for (var _i = 0, ops_1 = ops; _i < ops_1.length; _i++) {
            var op = ops_1[_i];
            var opType = types_core_1.SchemaObject.getTypeNameFor(op);
            // Check that the item(s) in question is not being deleted
            // Currently, AL cache is not stored/available locally
            if (opType === types_core_1.DeleteOperation.getTypeName()) {
                continue;
            }
            for (var _a = 0, _b = op.items; _a < _b.length; _a++) {
                var opItem = _b[_a];
                if (!opItem.body) {
                    continue;
                }
                for (var _c = 0, _d = this.localWorkflows; _c < _d.length; _c++) {
                    var workflowImplementation = _d[_c];
                    if (types_core_1.SchemaObject.matchesTypesFor(opItem.body, workflowImplementation.workflow.inputTypes)) {
                        var item = {
                            parentPath: op.parentPath,
                            id: opItem.id,
                            body: opItem.body,
                            revId: opItem.revId,
                            source: opItem.source
                        };
                        this.executeLocalWorkflow(workflowImplementation, [item], this.session.getClientMetadata()).catch(function (error) {
                            logging_1.Log.error(0x2224d0df /* tag_8jnd5 */, logging_1.LogCategory.CoreDefault, error);
                        });
                    }
                }
            }
        }
    };
    LocalWorkflowExecutionManager.prototype.getResourceAsArrayBuffer = function (modelId, version) {
        if (this.modelDownloader) {
            return this.modelDownloader.getResourceAsArrayBuffer(modelId, version);
        }
        else {
            return Promise.reject(new Error('Resource Downloader never created'));
        }
    };
    LocalWorkflowExecutionManager.prototype.getResourceAsURL = function (modelId, version) {
        if (this.modelDownloader) {
            return this.modelDownloader.getResourceAsURL(modelId, version);
        }
        else {
            return Promise.reject(new Error('Resource Downloader never created'));
        }
    };
    LocalWorkflowExecutionManager.prototype.getToken = function (_tokenType, _cb) {
        throw new Error('Not implemented');
    };
    LocalWorkflowExecutionManager.prototype.initWorkflow = function (workflowKind, workflowLambda) {
        if (workflowKind == interfaces_1.WorkflowKind.SingleItem) {
            var site = {
                getResourceAsArrayBuffer: this.getResourceAsArrayBuffer.bind(this),
                getResourceAsURL: this.getResourceAsURL.bind(this),
            };
            return workflowLambda.init(site);
        }
        else {
            return Promise.resolve();
        }
    };
    LocalWorkflowExecutionManager.prototype.executeLocalWorkflow = function (workflowInfo, inputItems, clientMetadata) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            var workflow = workflowInfo.workflow;
            var executeOp = new logging_1.Operation({
                operationName: 'ExecuteWorkflow',
                resourceId: workflow.id
            });
            executeOp.start();
            var endOp = function (error) {
                if (error) {
                    executeOp.resultDescription = error;
                    executeOp.resultSignature = 'Exception';
                    logging_1.Log.error(0x2224d0e0 /* tag_8jnd6 */, logging_1.LogCategory.CoreDefault, executeOp.stop());
                }
                else {
                    executeOp.success = true;
                    logging_1.Log.info(0x2224d0e1 /* tag_8jnd7 */, logging_1.LogCategory.CoreDefault, executeOp.stop());
                }
                return error;
            };
            var setAnnotations = function (obj, annotationType, annotations, options) {
                // Has to be true since only single-item workflows are currently supported
                if (inputItems[0].body !== obj) {
                    reject(endOp("Expected obj to be " + inputItems[0].body + " but instead it was " + obj));
                    return;
                }
                var item = inputItems[0];
                var error;
                // Check that annotations match expectations - otherwise workflow author must fix
                if (!Array.isArray(annotations)) {
                    error = "Workflow produced an invalid annotation array";
                }
                else if (!workflow.outputTypes || !workflow.outputTypes.includes(annotationType)) {
                    error = "Workflow said it would output one of [" + workflow.outputTypes + "] but instead output " + annotationType;
                }
                else if (!item) {
                    error = "No item provided";
                }
                else {
                    for (var _i = 0, annotations_1 = annotations; _i < annotations_1.length; _i++) {
                        var annotation = annotations_1[_i];
                        if (!types_core_1.SchemaObject.matchesTypesFor(annotation, [types_core_1.Annotation.getTypeName()])) {
                            error = "Workflow produced an output that is not an annotation " + types_core_1.SchemaObject.getTypeNameFor(annotation);
                        }
                        else if (types_core_1.SchemaObject.getTypeNameFor(annotation) !== annotationType) {
                            error = "Workflow produced inconsistent annotation types in setAnnotations call (" + types_core_1.SchemaObject.getTypeNameFor(annotation) + " did not match expected " + annotationType + ")";
                        }
                    }
                }
                if (error) {
                    reject(endOp(error));
                    return;
                }
                // Path to where the annotation will be added
                var path;
                if (options && options.isSessionAnnotation) {
                    path = ['session'];
                }
                else if (options && options.ancestorType) {
                    path = item.parentPath;
                }
                else {
                    path = item.parentPath.concat(item.id);
                }
                // Changing annotations into items
                var annotationItemsArray = [];
                for (var _a = 0, annotations_2 = annotations; _a < annotations_2.length; _a++) {
                    var annotation = annotations_2[_a];
                    annotationItemsArray.push({
                        id: _this.getNextClientAnnotationId(),
                        source: workflow.id,
                        body: annotation
                    });
                }
                _this.session.onAnnotationResults(new session_protocol_1.AnnotationResultsMessage({
                    annotationType: annotationType,
                    ops: [{ parentPath: path, items: annotationItemsArray }]
                }), function () { }); // Empty response callback because we don't need to acknowledge annotations are received
            };
            var submitSignals = function (signals) {
                // Changing signals into items
                var signalItemsArray = signals.map(function (signal) {
                    return {
                        id: _this.getNextClientSignalId(),
                        source: workflow.id,
                        body: signal
                    };
                });
                var submitOp = new types_core_1.SignalOperation({
                    parentPath: ['session'],
                    items: signalItemsArray
                });
                _this.session.submitOperations([submitOp]);
            };
            var context = {
                getToken: _this.getToken.bind(_this),
                clientMetadata: clientMetadata,
                site: {
                    getResourceAsArrayBuffer: _this.getResourceAsArrayBuffer.bind(_this),
                    getResourceAsURL: _this.getResourceAsURL.bind(_this),
                },
            };
            var site = {
                getResourceAsArrayBuffer: _this.getResourceAsArrayBuffer.bind(_this),
                getResourceAsURL: _this.getResourceAsURL.bind(_this),
            };
            var workflowExecutionDone = function (err) {
                endOp(err ? err.message : null);
                if (err) {
                    reject(err);
                }
                else {
                    resolve();
                }
            };
            var actions = {
                setAnnotations: setAnnotations,
                submitSignals: submitSignals,
                done: workflowExecutionDone,
                addAnnotation: function () { throw Error('Not implemented'); },
                addSiblingAnnotation: function () { throw Error('Not implemented'); },
                addSessionAnnotation: function () { throw Error('Not implemented'); },
            };
            var workflowInstance = workflowInfo.workflowLambda;
            if (workflow.kind === interfaces_1.WorkflowKind.SingleItem) {
                if (inputItems.length !== 1) {
                    endOp('Single item workflows expect a single input');
                }
                context.delta = inputItems[0].delta;
                try {
                    var singleItemWorkflow_1 = workflowInstance;
                    if (!workflowInfo.initPromise) {
                        workflowInfo.initPromise = singleItemWorkflow_1.init(site);
                    }
                    workflowInfo.initPromise.then(function () {
                        singleItemWorkflow_1.execute(inputItems[0].body, context, actions);
                    }).catch(function (error) {
                        endOp(error);
                    });
                }
                catch (error) {
                    endOp(error);
                }
            }
            else {
                endOp("Only single item workflows supported currently");
            }
        });
    };
    return LocalWorkflowExecutionManager;
}());
exports.LocalWorkflowExecutionManager = LocalWorkflowExecutionManager;
//# sourceMappingURL=local-workflow-execution-manager.js.map